import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { useSession, useAccess } from '@/lib/session';
import { useNavigate } from 'react-router-dom';
import { updateRestaurant, setOwnerPinHash, getWhatsAppConfig, saveWhatsAppConfig, type WhatsAppConfig, getLoyaltyConfig, saveLoyaltyConfig, type LoyaltyConfig, getEInvoiceConfig, saveEInvoiceConfig, type EInvoiceConfig } from '@/lib/data';
import { setAppPassword, hasAppPassword } from '@/lib/auth';
import { FEATURE_TIER, PLAN_LABEL, hasAccess, hasFeature, requiredTierFor, type Tier } from '@/lib/access';

type Printer = {
  id: string;
  name: string;
  /** 'network' (LAN/WiFi ESC/POS, port 9100) | 'windows' (USB on the POS PC) */
  type: string;
  /** IP for network printers, Windows printer name for USB */
  address: string;
  port: number;
  /** 'Billing' or a kitchen station — drives KOT routing on the POS */
  role: string;
  paper: number; // 80 | 58 (mm)
  enabled: boolean;
  // legacy keys kept for older rows
  ip?: string;
  location?: string;
  status?: string;
};

const PRINTER_ROLES = ['Billing', 'Tandoor', 'Main', 'Cold', 'Dessert'];

const emptyPrinter = {
  name: '',
  type: 'network',
  address: '',
  port: 9100,
  role: 'Billing',
  paper: 80,
};

function str(s: Record<string, unknown>, key: string): string {
  const v = s[key];
  return typeof v === 'string' ? v : '';
}

function num(s: Record<string, unknown>, key: string): number {
  const v = s[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function bool(s: Record<string, unknown>, key: string, fallback: boolean): boolean {
  const v = s[key];
  return typeof v === 'boolean' ? v : fallback;
}

function parsePrinters(s: Record<string, unknown>): Printer[] {
  const raw = s.printers;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (p): p is Record<string, unknown> =>
        !!p && typeof p === 'object' && typeof (p as Printer).name === 'string' && typeof (p as Printer).id === 'string',
    )
    .map((p): Printer => {
      // Normalise legacy rows ({type:'Thermal', ip, location}) to the canonical shape.
      const legacyType = typeof p.type === 'string' ? p.type.toLowerCase() : '';
      const isWindows = legacyType === 'windows' || legacyType === 'usb';
      return {
        id: String(p.id),
        name: String(p.name),
        type: isWindows ? 'windows' : 'network',
        address: typeof p.address === 'string' && p.address ? p.address : typeof p.ip === 'string' ? p.ip : '',
        port: typeof p.port === 'number' ? p.port : 9100,
        role:
          typeof p.role === 'string' && p.role
            ? p.role
            : typeof p.location === 'string' && PRINTER_ROLES.includes(p.location)
              ? p.location
              : 'Billing',
        paper: p.paper === 58 ? 58 : 80,
        enabled: typeof p.enabled === 'boolean' ? p.enabled : true,
        status: typeof p.status === 'string' ? p.status : undefined,
      };
    });
}

export default function SettingsPage() {
  const { restaurant, refreshRestaurant } = useSession();
  const access = useAccess();
  const navigate = useNavigate();
  const settings = (restaurant.settings ?? {}) as Record<string, unknown>;

  // A settings tab is locked when its feature tier is above the current plan.
  const tabLocked = (feature: string): Tier | null => {
    const required = FEATURE_TIER[feature];
    if (!required) return null;
    return hasAccess(access.effectiveTier, required) ? null : required;
  };

  const [activeSection, setActiveSection] = useState<'profile' | 'tax' | 'printers' | 'branding' | 'channels' | 'whatsapp' | 'loyalty' | 'einvoice' | 'apps'>('profile');
  const [profile, setProfile] = useState({
    name: restaurant.name,
    displayName: str(settings, 'displayName'),
    phone: restaurant.phone ?? '',
    email: str(settings, 'email'),
    website: str(settings, 'website'),
    address: restaurant.address ?? '',
    cuisines: str(settings, 'cuisines'),
    avgCostForTwo: num(settings, 'avgCostForTwo'),
    gstin: restaurant.gstin ?? '',
    fssai: restaurant.fssai ?? '',
    openingTime: str(settings, 'openingTime'),
    closingTime: str(settings, 'closingTime'),
    description: str(settings, 'description'),
  });
  const [tax, setTax] = useState({
    gstEnabled: bool(settings, 'gstEnabled', restaurant.gstRate > 0),
    cgstRate: restaurant.gstRate / 2,
    sgstRate: restaurant.gstRate / 2,
    // false = GST added on top of menu prices (exclusive, default).
    // true  = menu prices already include GST (inclusive).
    gstInclusive: bool(settings, 'gstInclusive', false),
    serviceChargeEnabled: bool(settings, 'serviceChargeEnabled', false),
    serviceChargeRate: num(settings, 'serviceChargeRate'),
  });
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [printers, setPrinters] = useState<Printer[]>(parsePrinters(settings));

  // Ordering channels, grouped by category. In-restaurant + Direct channels
  // default ON (existing outlets keep taking orders); aggregators/hotel default
  // OFF until onboarded. Turning a channel OFF blocks new orders on that channel
  // (enforced server-side in place_self_order; aggregator ingest also checks it).
  const aggrSettings = (settings.aggregators ?? {}) as Record<string, unknown>;
  const [channels, setChannels] = useState({
    // In-restaurant
    DineIn: bool(aggrSettings, 'DineIn', true),
    Takeaway: bool(aggrSettings, 'Takeaway', true),
    QR: bool(aggrSettings, 'QR', true),
    // Direct
    Website: bool(aggrSettings, 'Website', true),
    // Online aggregators
    Swiggy: bool(aggrSettings, 'Swiggy', false),
    Zomato: bool(aggrSettings, 'Zomato', false),
    ONDC: bool(aggrSettings, 'ONDC', false),
    // Hotel
    Hotel: bool(aggrSettings, 'Hotel', false),
  });
  type ChannelKey = keyof typeof channels;
  const channelGroups: { title: string; note: string; feature?: string; items: { key: ChannelKey; label: string; desc: string; icon?: string; color?: string; abbr?: string; feature?: string }[] }[] = [
    {
      title: 'In-restaurant',
      note: 'Orders taken inside your outlet.',
      items: [
        { key: 'DineIn', label: 'Dine-in', desc: 'Table service orders (POS & Captain)', icon: 'ri-restaurant-2-line', color: '#0F766E' },
        { key: 'Takeaway', label: 'Takeaway', desc: 'Counter / pickup orders', icon: 'ri-takeaway-line', color: '#B45309' },
        { key: 'QR', label: 'QR table ordering', desc: 'Guests scan the table QR to order', icon: 'ri-qr-code-line', color: '#4F46E5', feature: 'qr-ordering' },
      ],
    },
    {
      title: 'Direct online',
      note: 'Your own ordering link — no commission.',
      items: [
        { key: 'Website', label: 'Website / self-order', desc: 'Public /r link self-ordering', icon: 'ri-global-line', color: '#0EA5E9', feature: 'website' },
      ],
    },
    {
      title: 'Food aggregators',
      note: "Turn on once the channel is onboarded. When off, its incoming orders are rejected.",
      items: [
        { key: 'Swiggy', label: 'Swiggy', desc: 'Swiggy partner orders', color: '#F58220', abbr: 'SW' },
        { key: 'Zomato', label: 'Zomato', desc: 'Zomato partner orders', color: '#E23744', abbr: 'ZO' },
        { key: 'ONDC', label: 'ONDC', desc: 'ONDC network orders', color: '#1B6CA8', abbr: 'ON' },
      ],
    },
    {
      title: 'Hotel room service (ezypms)',
      note: 'Appear on nearby hotels’ in-room QR menus (within 2 km).',
      feature: 'hotel',
      items: [
        { key: 'Hotel', label: 'Accept hotel room orders', desc: 'Receive room-service orders from nearby hotels', icon: 'ri-hotel-line', color: '#0F766E' },
      ],
    },
  ];

  // Branding state
  const [selectedTheme, setSelectedTheme] = useState(str(settings, 'qrTheme') || 'Teal');
  const [logoPreview, setLogoPreview] = useState<string | null>(str(settings, 'logo') || null);
  const [receiptFooter, setReceiptFooter] = useState(restaurant.billFooter ?? '');
  // UPI payment QR — the POS/Captain/customer-display generate a scannable
  // QR from this UPI ID (with the exact amount). Stored in restaurants.settings.
  const [upiId, setUpiId] = useState(str(settings, 'upiId'));
  const [showUpiQr, setShowUpiQr] = useState(bool(settings, 'showUpiQr', true));

  // Add Printer modal
  const [printerModalOpen, setPrinterModalOpen] = useState(false);
  const [printerForm, setPrinterForm] = useState({ ...emptyPrinter });

  const sections = [
    { key: 'profile' as const, icon: 'ri-store-2-line', label: 'Outlet Profile' },
    { key: 'tax' as const, icon: 'ri-bill-line', label: 'Tax & Billing' },
    { key: 'printers' as const, icon: 'ri-printer-line', label: 'Printers' },
    { key: 'branding' as const, icon: 'ri-palette-line', label: 'Branding' },
    { key: 'channels' as const, icon: 'ri-takeaway-line', label: 'Channels' },
    { key: 'loyalty' as const, icon: 'ri-gift-line', label: 'Loyalty' },
    { key: 'apps' as const, icon: 'ri-smartphone-line', label: 'App Login' },
  ];

  // E-Invoice (GST IRN)
  const [einv, setEinv] = useState<EInvoiceConfig>(getEInvoiceConfig(restaurant));
  const saveEInvoice = async () => {
    try {
      setSaving(true);
      await saveEInvoiceConfig(restaurant, einv);
      await refreshRestaurant();
      flash('E-Invoice settings saved.');
    } catch (e) {
      flashError(e instanceof Error ? e.message : 'Could not save e-invoice settings.');
    } finally {
      setSaving(false);
    }
  };

  // WhatsApp (WABA) order updates
  const [waba, setWaba] = useState<WhatsAppConfig>(getWhatsAppConfig(restaurant));
  const saveWhatsApp = async () => {
    try {
      setSaving(true);
      await saveWhatsAppConfig(restaurant, waba);
      await refreshRestaurant();
      flash('WhatsApp settings saved.');
    } catch (e) {
      flashError(e instanceof Error ? e.message : 'Could not save WhatsApp settings.');
    } finally {
      setSaving(false);
    }
  };

  // Loyalty program
  const [loyalty, setLoyalty] = useState<LoyaltyConfig>(getLoyaltyConfig(restaurant));
  const saveLoyalty = async () => {
    try {
      setSaving(true);
      await saveLoyaltyConfig(restaurant, loyalty);
      await refreshRestaurant();
      flash('Loyalty settings saved.');
    } catch (e) {
      flashError(e instanceof Error ? e.message : 'Could not save loyalty settings.');
    } finally {
      setSaving(false);
    }
  };

  // App password (POS / Captain apps)
  const [appPwd, setAppPwd] = useState({ email: str(settings, 'email'), password: '', confirm: '' });
  const [appPwdSet, setAppPwdSet] = useState(false);
  const [appPwdBusy, setAppPwdBusy] = useState(false);
  useEffect(() => { hasAppPassword().then(setAppPwdSet).catch(() => {}); }, []);

  // Owner master PIN (opens the POS / Captain apps without a staff row)
  const [ownerPin, setOwnerPin] = useState({ pin: restaurant.ownerPin ?? '', confirm: restaurant.ownerPin ?? '' });
  const [ownerPinBusy, setOwnerPinBusy] = useState(false);

  const saveOwnerPin = async () => {
    const pin = ownerPin.pin.trim();
    if (!/^\d{4}$/.test(pin)) { flashError('Owner PIN must be exactly 4 digits.'); return; }
    if (pin !== ownerPin.confirm.trim()) { flashError('PINs do not match.'); return; }
    setOwnerPinBusy(true);
    try {
      await updateRestaurant(restaurant.id, { ownerPin: pin });
      await setOwnerPinHash(pin); // also store a bcrypt hash (apps verify via RPC)
      await refreshRestaurant();
      flash('Owner PIN saved. Use it to open the POS / Captain apps as the owner.');
    } catch (e) {
      flashError(e instanceof Error ? e.message : 'Could not save the owner PIN.');
    } finally {
      setOwnerPinBusy(false);
    }
  };

  const saveAppPassword = async () => {
    if (!appPwd.email.trim()) { flashError('Enter an email address.'); return; }
    if (appPwd.password.length < 6) { flashError('Password must be at least 6 characters.'); return; }
    if (appPwd.password !== appPwd.confirm) { flashError('Passwords do not match.'); return; }
    setAppPwdBusy(true);
    try {
      await setAppPassword(appPwd.email.trim(), appPwd.password);
      setAppPwdSet(true);
      setAppPwd((p) => ({ ...p, password: '', confirm: '' }));
      flash('App login saved. Use this email & password in the POS / Captain apps.');
    } catch (e) {
      flashError(e instanceof Error ? e.message : 'Could not save the app password.');
    } finally {
      setAppPwdBusy(false);
    }
  };

  const flash = (msg: string) => {
    setSaveMessage(msg);
    setTimeout(() => setSaveMessage(null), 2500);
  };

  const flashError = (msg: string) => {
    setSaveError(msg);
    setTimeout(() => setSaveError(null), 3500);
  };

  /** Latest persisted settings, used as a merge base for every save. */
  const baseSettings = () => ({ ...((restaurant.settings ?? {}) as Record<string, unknown>) });

  const saveProfile = async () => {
    if (!profile.name.trim()) { flashError('Outlet name is required.'); return; }
    try {
      setSaving(true);
      await updateRestaurant(restaurant.id, {
        name: profile.name.trim(),
        address: profile.address.trim() || null,
        phone: profile.phone.trim() || null,
        gstin: profile.gstin.trim() || null,
        fssai: profile.fssai.trim() || null,
        settings: {
          ...baseSettings(),
          displayName: profile.displayName,
          email: profile.email,
          website: profile.website,
          cuisines: profile.cuisines,
          avgCostForTwo: profile.avgCostForTwo,
          openingTime: profile.openingTime,
          closingTime: profile.closingTime,
          description: profile.description,
        },
      });
      await refreshRestaurant();
      flash('Profile settings saved successfully!');
    } catch (e) {
      flashError(e instanceof Error ? e.message : 'Could not save the profile.');
    } finally {
      setSaving(false);
    }
  };

  const resetProfile = () => {
    const s = (restaurant.settings ?? {}) as Record<string, unknown>;
    setProfile({
      name: restaurant.name,
      displayName: str(s, 'displayName'),
      phone: restaurant.phone ?? '',
      email: str(s, 'email'),
      website: str(s, 'website'),
      address: restaurant.address ?? '',
      cuisines: str(s, 'cuisines'),
      avgCostForTwo: num(s, 'avgCostForTwo'),
      gstin: restaurant.gstin ?? '',
      fssai: restaurant.fssai ?? '',
      openingTime: str(s, 'openingTime'),
      closingTime: str(s, 'closingTime'),
      description: str(s, 'description'),
    });
  };

  const saveTax = async () => {
    try {
      setSaving(true);
      await updateRestaurant(restaurant.id, {
        gstRate: tax.gstEnabled ? tax.cgstRate + tax.sgstRate : 0,
        settings: {
          ...baseSettings(),
          gstEnabled: tax.gstEnabled,
          gstInclusive: tax.gstInclusive,
          serviceChargeEnabled: tax.serviceChargeEnabled,
          serviceChargeRate: tax.serviceChargeRate,
        },
      });
      await refreshRestaurant();
      flash('Tax settings saved successfully!');
    } catch (e) {
      flashError(e instanceof Error ? e.message : 'Could not save tax settings.');
    } finally {
      setSaving(false);
    }
  };

  const persistPrinters = async (next: Printer[], successMsg: string) => {
    const prev = printers;
    setPrinters(next);
    try {
      setSaving(true);
      await updateRestaurant(restaurant.id, {
        settings: { ...baseSettings(), printers: next },
      });
      await refreshRestaurant();
      flash(successMsg);
    } catch (e) {
      setPrinters(prev);
      flashError(e instanceof Error ? e.message : 'Could not save printers.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPrinter = async () => {
    if (!printerForm.name.trim() || !printerForm.address.trim()) return;
    const newPrinter: Printer = {
      id: `prn-${Date.now()}`,
      name: printerForm.name.trim(),
      type: printerForm.type,
      address: printerForm.address.trim(),
      port: Number(printerForm.port) || 9100,
      role: printerForm.role,
      paper: printerForm.paper === 58 ? 58 : 80,
      enabled: true,
      // legacy keys so older app builds still render the list
      ip: printerForm.type === 'network' ? printerForm.address.trim() : '',
      location: printerForm.role,
      status: 'Online',
    };
    setPrinterModalOpen(false);
    setPrinterForm({ ...emptyPrinter });
    await persistPrinters([...printers, newPrinter], 'Printer added successfully!');
  };

  const handleRemovePrinter = async (printer: Printer) => {
    await persistPrinters(printers.filter((p) => p.id !== printer.id), `${printer.name} removed`);
  };

  const saveBranding = async () => {
    try {
      setSaving(true);
      await updateRestaurant(restaurant.id, {
        billFooter: receiptFooter.trim() || null,
        settings: {
          ...baseSettings(),
          qrTheme: selectedTheme,
          logo: logoPreview,
          upiId: upiId.trim(),
          showUpiQr,
        },
      });
      await refreshRestaurant();
      flash('Branding settings saved successfully!');
    } catch (e) {
      flashError(e instanceof Error ? e.message : 'Could not save branding.');
    } finally {
      setSaving(false);
    }
  };

  const saveChannels = async () => {
    try {
      setSaving(true);
      await updateRestaurant(restaurant.id, {
        settings: { ...baseSettings(), aggregators: channels },
      });
      await refreshRestaurant();
      flash('Online channels updated.');
    } catch (e) {
      flashError(e instanceof Error ? e.message : 'Could not save channels.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const themes = ['Teal', 'Warm', 'Minimal'];

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-foreground-800">Settings</h2>
        <p className="text-sm text-foreground-500 mt-0.5">Manage outlet configuration, tax, printers, and branding</p>
      </div>

      {/* Save Toast */}
      {saveMessage && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-2 shadow-sm animate-[slideInRight_0.25s_ease-out]">
          <i className="ri-checkbox-circle-line text-green-500"></i>
          {saveMessage}
        </div>
      )}

      {/* Error Toast */}
      {saveError && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2 shadow-sm animate-[slideInRight_0.25s_ease-out]">
          <i className="ri-error-warning-line text-red-500"></i>
          {saveError}
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit overflow-x-auto">
        {sections.map((s) => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              activeSection === s.key
                ? 'bg-primary-500 text-background-50'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${s.icon} text-base`}></i>
            <span>{s.label}</span>
          </button>
        ))}
      </div>

      {/* Profile Section */}
      {activeSection === 'profile' && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 space-y-5 max-w-2xl">
          <h3 className="font-semibold text-foreground-800 text-base">Outlet Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Outlet Name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} />
            <FormField label="Display Name" value={profile.displayName} onChange={(v) => setProfile({ ...profile, displayName: v })} />
            <FormField label="Phone" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} />
            <FormField label="Email" value={profile.email} onChange={(v) => setProfile({ ...profile, email: v })} />
            <FormField label="Website" value={profile.website} onChange={(v) => setProfile({ ...profile, website: v })} />
            <FormField label="Cuisines" value={profile.cuisines} onChange={(v) => setProfile({ ...profile, cuisines: v })} />
          </div>
          <div className="space-y-3">
            <FormField label="Address" value={profile.address} onChange={(v) => setProfile({ ...profile, address: v })} />
            <FormField label="Description" value={profile.description} onChange={(v) => setProfile({ ...profile, description: v })} isTextarea />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FormField label="Avg Cost for Two (₹)" value={String(profile.avgCostForTwo)} onChange={(v) => setProfile({ ...profile, avgCostForTwo: Number(v) })} type="number" />
            <FormField label="Opening Time" value={profile.openingTime} onChange={(v) => setProfile({ ...profile, openingTime: v })} type="time" />
            <FormField label="Closing Time" value={profile.closingTime} onChange={(v) => setProfile({ ...profile, closingTime: v })} type="time" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="GSTIN" value={profile.gstin} onChange={(v) => setProfile({ ...profile, gstin: v })} />
            <FormField label="FSSAI License" value={profile.fssai} onChange={(v) => setProfile({ ...profile, fssai: v })} />
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-background-200">
            <button
              onClick={saveProfile}
              disabled={saving}
              className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
            <button
              onClick={resetProfile}
              className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Channels Section */}
      {activeSection === 'channels' && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 space-y-6 max-w-2xl">
          <div>
            <h3 className="font-semibold text-foreground-800 text-base">Ordering Channels</h3>
            <p className="text-sm text-foreground-500 mt-1">
              Turn any channel off to immediately stop taking new orders from it.
              Off channels are rejected across the POS, KDS, Captain and your
              QR/website ordering.
            </p>
          </div>

          {channelGroups.map((group) => {
            const onCount = group.items.filter((it) => channels[it.key]).length;
            return (
              <div key={group.title}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-foreground-700">{group.title}</p>
                  <span className="text-[11px] text-foreground-400">{onCount}/{group.items.length} on</span>
                </div>
                <p className="text-xs text-foreground-500 mb-3">{group.note}</p>
                <div className="space-y-2.5">
                  {group.items.map((c) => {
                    // A channel is plan-locked when its (item- or group-level)
                    // feature tier is above the current plan. Locked → toggle
                    // disabled + an inline Upgrade affordance.
                    const feat = c.feature ?? group.feature;
                    const locked = feat ? !hasFeature(access.effectiveTier, feat) : false;
                    const lockTier = feat ? (requiredTierFor(feat) ?? 'growth') : 'growth';
                    return (
                    <div key={c.key} className={`p-3.5 bg-background-100 rounded-lg flex items-center justify-between ${locked ? 'opacity-80' : ''}`}>
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-lg flex items-center justify-center text-background-50 font-bold text-xs shrink-0" style={{ backgroundColor: c.color ?? '#666' }}>
                          {c.icon ? <i className={c.icon}></i> : (c.abbr ?? c.label.slice(0, 2).toUpperCase())}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-foreground-800">{c.label}</p>
                          <p className="text-xs text-foreground-500">{c.desc}</p>
                        </div>
                      </div>
                      {locked ? (
                        <button
                          onClick={() => navigate('/dashboard/billing')}
                          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-accent-300 text-accent-600 hover:bg-accent-50 cursor-pointer whitespace-nowrap"
                          title={`Available on the ${PLAN_LABEL[lockTier]} plan`}
                        >
                          <i className="ri-lock-2-line"></i>Upgrade to {PLAN_LABEL[lockTier]}
                        </button>
                      ) : (
                        <button
                          onClick={() => setChannels({ ...channels, [c.key]: !channels[c.key] })}
                          className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${channels[c.key] ? 'bg-primary-500' : 'bg-background-300'}`}
                          aria-label={`Toggle ${c.label}`}
                          title={channels[c.key] ? 'On — accepting orders' : 'Off — orders blocked'}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${channels[c.key] ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex items-center gap-3 pt-2 border-t border-background-200">
            <button
              onClick={saveChannels}
              disabled={saving}
              className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Channels'}
            </button>
          </div>
        </div>
      )}

      {activeSection === 'whatsapp' && tabLocked('whatsapp') && (
        <UpgradeNote required={tabLocked('whatsapp')!} feature="WhatsApp order updates" onUpgrade={() => navigate('/dashboard/billing')} />
      )}
      {activeSection === 'whatsapp' && !tabLocked('whatsapp') && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 space-y-5 max-w-2xl">
          <div>
            <h3 className="font-semibold text-foreground-800 text-base">WhatsApp Order Updates</h3>
            <p className="text-sm text-foreground-500 mt-1">
              Send customers an automatic WhatsApp message when their order is
              placed, ready, or billed — via the Meta WhatsApp Cloud API. Connect
              your WABA phone number below and approve the message templates in
              Meta Business Manager.
            </p>
          </div>

          <div className="p-4 bg-background-100 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center text-background-50" style={{ backgroundColor: '#25D366' }}>
                <i className="ri-whatsapp-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-800">Enable WhatsApp updates</p>
                <p className="text-xs text-foreground-500">Master switch for all WhatsApp messages.</p>
              </div>
            </div>
            <button
              onClick={() => setWaba({ ...waba, enabled: !waba.enabled })}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${waba.enabled ? 'bg-primary-500' : 'bg-background-300'}`}
              aria-label="Toggle WhatsApp updates"
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${waba.enabled ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-foreground-700">WABA Phone Number ID</span>
              <input
                value={waba.phoneNumberId}
                onChange={(e) => setWaba({ ...waba, phoneNumberId: e.target.value })}
                placeholder="e.g. 109876543210987"
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-foreground-700">Template Language</span>
              <input
                value={waba.languageCode}
                onChange={(e) => setWaba({ ...waba, languageCode: e.target.value })}
                placeholder="en"
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-foreground-700">Permanent Access Token</span>
            <input
              type="password"
              value={waba.accessToken}
              onChange={(e) => setWaba({ ...waba, accessToken: e.target.value })}
              placeholder="EAAG…"
              className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500"
            />
            <span className="text-xs text-foreground-500 mt-1 block">
              Stored encrypted in your outlet settings. Leave blank to use the platform-wide token.
            </span>
          </label>

          <div className="pt-2 border-t border-background-200">
            <p className="text-sm font-medium text-foreground-700 mb-3 mt-2">Send a message when an order is…</p>
            {([
              { key: 'placed' as const, label: 'Placed', desc: 'Order confirmed' },
              { key: 'ready' as const, label: 'Ready', desc: 'Ready to serve / collect' },
              { key: 'bill' as const, label: 'Billed', desc: 'Bill / payment receipt' },
            ]).map((ev) => (
              <div key={ev.key} className="p-3 bg-background-100 rounded-lg flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-foreground-800">{ev.label}</p>
                  <p className="text-xs text-foreground-500">{ev.desc}</p>
                </div>
                <button
                  onClick={() => setWaba({ ...waba, events: { ...waba.events, [ev.key]: !waba.events[ev.key] } })}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${waba.events[ev.key] ? 'bg-primary-500' : 'bg-background-300'}`}
                  aria-label={`Toggle ${ev.label}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${waba.events[ev.key] ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
                </button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-background-200">
            <button
              onClick={saveWhatsApp}
              disabled={saving}
              className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save WhatsApp Settings'}
            </button>
          </div>
        </div>
      )}

      {activeSection === 'loyalty' && tabLocked('loyalty') && (
        <UpgradeNote required={tabLocked('loyalty')!} feature="the loyalty program" onUpgrade={() => navigate('/dashboard/billing')} />
      )}
      {activeSection === 'loyalty' && !tabLocked('loyalty') && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 space-y-5 max-w-2xl">
          <div>
            <h3 className="font-semibold text-foreground-800 text-base">Loyalty Program</h3>
            <p className="text-sm text-foreground-500 mt-1">
              Reward repeat customers. Points are earned automatically when a
              paid order is matched to a customer by phone number, and can be
              redeemed against future bills.
            </p>
          </div>

          <div className="p-4 bg-background-100 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center text-background-50" style={{ backgroundColor: '#7C3AED' }}>
                <i className="ri-gift-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-800">Enable loyalty program</p>
                <p className="text-xs text-foreground-500">Master switch for earning &amp; redeeming.</p>
              </div>
            </div>
            <button
              onClick={() => setLoyalty({ ...loyalty, enabled: !loyalty.enabled })}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${loyalty.enabled ? 'bg-primary-500' : 'bg-background-300'}`}
              aria-label="Toggle loyalty program"
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${loyalty.enabled ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-foreground-700">Points earned per ₹100</span>
              <input
                type="number" min={0} step="0.5"
                value={loyalty.earnRate}
                onChange={(e) => setLoyalty({ ...loyalty, earnRate: Number(e.target.value) })}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-foreground-700">₹ value of 1 point</span>
              <input
                type="number" min={0} step="0.5"
                value={loyalty.redeemValue}
                onChange={(e) => setLoyalty({ ...loyalty, redeemValue: Number(e.target.value) })}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-foreground-700">Min. points to redeem</span>
              <input
                type="number" min={0} step="10"
                value={loyalty.minRedeemPoints}
                onChange={(e) => setLoyalty({ ...loyalty, minRedeemPoints: Number(e.target.value) })}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-foreground-700">Max redemption (% of bill)</span>
              <input
                type="number" min={0} max={100} step="5"
                value={loyalty.maxRedeemPct}
                onChange={(e) => setLoyalty({ ...loyalty, maxRedeemPct: Number(e.target.value) })}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </label>
          </div>

          <p className="text-xs text-foreground-500 bg-background-100 rounded-lg p-3">
            Example: at {loyalty.earnRate} pt / ₹100, a ₹500 bill earns{' '}
            <span className="font-medium text-foreground-700">{Math.floor(500 / 100 * loyalty.earnRate)} points</span>.
            {' '}{loyalty.minRedeemPoints} points are worth ₹{loyalty.minRedeemPoints * loyalty.redeemValue} at redemption.
          </p>

          <div className="pt-2 border-t border-background-200">
            <div className="flex items-center justify-between mb-2 mt-2">
              <p className="text-sm font-medium text-foreground-700">Membership tiers</p>
              <button
                onClick={() => setLoyalty({ ...loyalty, tiers: [...loyalty.tiers, { name: 'New Tier', minPoints: 0 }] })}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-add-line"></i> Add tier
              </button>
            </div>
            <p className="text-xs text-foreground-500 mb-3">A customer is placed in the highest tier whose point threshold they meet.</p>
            {loyalty.tiers.map((t, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <input
                  value={t.name}
                  onChange={(e) => { const tiers = [...loyalty.tiers]; tiers[i] = { ...t, name: e.target.value }; setLoyalty({ ...loyalty, tiers }); }}
                  placeholder="Tier name"
                  className="flex-1 px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500"
                />
                <input
                  type="number" min={0}
                  value={t.minPoints}
                  onChange={(e) => { const tiers = [...loyalty.tiers]; tiers[i] = { ...t, minPoints: Number(e.target.value) }; setLoyalty({ ...loyalty, tiers }); }}
                  placeholder="Min pts"
                  className="w-28 px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500"
                />
                <button
                  onClick={() => setLoyalty({ ...loyalty, tiers: loyalty.tiers.filter((_, j) => j !== i) })}
                  className="text-foreground-400 hover:text-red-600 cursor-pointer px-1"
                  aria-label="Remove tier"
                ><i className="ri-delete-bin-line"></i></button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-background-200">
            <button
              onClick={saveLoyalty}
              disabled={saving}
              className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Loyalty Settings'}
            </button>
          </div>
        </div>
      )}

      {activeSection === 'einvoice' && tabLocked('einvoice') && (
        <UpgradeNote required={tabLocked('einvoice')!} feature="e-invoicing (GST IRN)" onUpgrade={() => navigate('/dashboard/billing')} />
      )}
      {activeSection === 'einvoice' && !tabLocked('einvoice') && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 space-y-5 max-w-2xl">
          <div>
            <h3 className="font-semibold text-foreground-800 text-base">E-Invoice (GST IRN)</h3>
            <p className="text-sm text-foreground-500 mt-1">
              Generate government e-invoices (IRN + signed QR) via your GSP. Required
              only above the GST e-invoicing turnover threshold. Your GSTIN ({restaurant.gstin || 'not set'})
              is taken from the Outlet Profile; GSP credentials are configured server-side.
            </p>
          </div>

          <div className="p-4 bg-background-100 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center text-background-50" style={{ backgroundColor: '#0EA5E9' }}>
                <i className="ri-receipt-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-800">Enable e-invoicing</p>
                <p className="text-xs text-foreground-500">Show the "Generate IRN" action on orders.</p>
              </div>
            </div>
            <button
              onClick={() => setEinv({ ...einv, enabled: !einv.enabled })}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer shrink-0 ${einv.enabled ? 'bg-primary-500' : 'bg-background-300'}`}
              aria-label="Toggle e-invoicing"
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${einv.enabled ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-foreground-700">Legal name (as per GST)</span>
              <input value={einv.legalName} onChange={(e) => setEinv({ ...einv, legalName: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-sm font-medium text-foreground-700">Registered address</span>
              <input value={einv.address} onChange={(e) => setEinv({ ...einv, address: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-foreground-700">City</span>
              <input value={einv.city} onChange={(e) => setEinv({ ...einv, city: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-foreground-700">Pincode</span>
              <input value={einv.pincode} onChange={(e) => setEinv({ ...einv, pincode: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-foreground-700">GST state code (2-digit)</span>
              <input value={einv.stateCode} onChange={(e) => setEinv({ ...einv, stateCode: e.target.value })} placeholder="e.g. 24"
                className="mt-1 w-full px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-500" />
            </label>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-background-200">
            <button onClick={saveEInvoice} disabled={saving}
              className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
              {saving ? 'Saving…' : 'Save E-Invoice Settings'}
            </button>
          </div>
        </div>
      )}

      {/* App Login Section */}
      {activeSection === 'apps' && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 space-y-5 max-w-2xl">
          <h3 className="font-semibold text-foreground-800 text-base">Login &amp; Password</h3>
          <p className="text-sm text-foreground-500">
            One email &amp; password signs you in everywhere — this dashboard, the EzyDine POS
            (Windows / tablet) and the Captain app. Change your password here.
          </p>
          {appPwdSet && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <i className="ri-checkbox-circle-line"></i>
              App login is already set up. Saving below changes the password.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Login Email" value={appPwd.email} onChange={(v) => setAppPwd({ ...appPwd, email: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Password" value={appPwd.password} onChange={(v) => setAppPwd({ ...appPwd, password: v })} type="password" />
            <FormField label="Confirm Password" value={appPwd.confirm} onChange={(v) => setAppPwd({ ...appPwd, confirm: v })} type="password" />
          </div>
          <div className="flex items-center gap-3 pt-2 border-t border-background-200">
            <button
              onClick={saveAppPassword}
              disabled={appPwdBusy}
              className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {appPwdBusy ? 'Saving…' : appPwdSet ? 'Change Password' : 'Set App Login'}
            </button>
          </div>

          {/* Owner master PIN */}
          <div className="pt-5 border-t border-background-200 space-y-4">
            <div>
              <h3 className="font-semibold text-foreground-800 text-base">Owner Master PIN</h3>
              <p className="text-sm text-foreground-500 mt-1">
                A 4-digit PIN that opens the POS &amp; Captain apps as the owner — even when staff PINs exist.
                Without it you'd be locked out of the device once you add your first staff member.
                It also authorises manager-only actions (voids, discounts) on the POS.
              </p>
            </div>
            {restaurant.ownerPin && (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <i className="ri-shield-check-line"></i>
                Owner PIN is set. Saving below changes it.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Owner PIN (4 digits)" value={ownerPin.pin} onChange={(v) => setOwnerPin({ ...ownerPin, pin: v.replace(/\D/g, '').slice(0, 4) })} />
              <FormField label="Confirm PIN" value={ownerPin.confirm} onChange={(v) => setOwnerPin({ ...ownerPin, confirm: v.replace(/\D/g, '').slice(0, 4) })} />
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-background-200">
              <button
                onClick={saveOwnerPin}
                disabled={ownerPinBusy}
                className="px-5 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {ownerPinBusy ? 'Saving…' : restaurant.ownerPin ? 'Change Owner PIN' : 'Set Owner PIN'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Section */}
      {activeSection === 'tax' && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 space-y-5 max-w-2xl">
          <h3 className="font-semibold text-foreground-800 text-base">Tax &amp; Billing Configuration</h3>

          <div className="p-4 bg-background-100 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-foreground-700">Enable GST</label>
              <button
                onClick={() => setTax({ ...tax, gstEnabled: !tax.gstEnabled })}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${tax.gstEnabled ? 'bg-primary-500' : 'bg-background-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${tax.gstEnabled ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
              </button>
            </div>
            {tax.gstEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                <FormField
                  label="CGST Rate (%)"
                  value={String(tax.cgstRate)}
                  onChange={(v) => setTax({ ...tax, cgstRate: Number(v) })}
                  type="number"
                  step="0.5"
                />
                <FormField
                  label="SGST Rate (%)"
                  value={String(tax.sgstRate)}
                  onChange={(v) => setTax({ ...tax, sgstRate: Number(v) })}
                  type="number"
                  step="0.5"
                />
              </div>
            )}
          </div>

          {tax.gstEnabled && (
            <div className="p-4 bg-background-100 rounded-lg">
              <label className="text-sm font-medium text-foreground-700">GST pricing mode</label>
              <p className="text-xs text-foreground-500 mt-1 mb-3">
                How your menu prices relate to GST. This affects every bill (POS, Captain & online).
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => setTax({ ...tax, gstInclusive: false })}
                  className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${!tax.gstInclusive ? 'border-primary-500 bg-primary-50' : 'border-background-300 bg-background-50 hover:border-background-400'}`}
                >
                  <span className="block text-sm font-semibold text-foreground-800">GST exclusive</span>
                  <span className="block text-xs text-foreground-500 mt-0.5">GST is added on top of the menu price. e.g. ₹100 item → ₹{(100 * (1 + (tax.cgstRate + tax.sgstRate) / 100)).toFixed(0)} payable.</span>
                </button>
                <button
                  onClick={() => setTax({ ...tax, gstInclusive: true })}
                  className={`text-left p-3 rounded-lg border transition-colors cursor-pointer ${tax.gstInclusive ? 'border-primary-500 bg-primary-50' : 'border-background-300 bg-background-50 hover:border-background-400'}`}
                >
                  <span className="block text-sm font-semibold text-foreground-800">GST inclusive</span>
                  <span className="block text-xs text-foreground-500 mt-0.5">Menu price already includes GST. e.g. ₹100 item → ₹100 payable, tax shown as extracted.</span>
                </button>
              </div>
            </div>
          )}

          <div className="p-4 bg-background-100 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-foreground-700">Service Charge</label>
              <button
                onClick={() => setTax({ ...tax, serviceChargeEnabled: !tax.serviceChargeEnabled })}
                className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${tax.serviceChargeEnabled ? 'bg-primary-500' : 'bg-background-300'}`}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-transform ${tax.serviceChargeEnabled ? 'translate-x-5' : 'translate-x-0.5'}`}></span>
              </button>
            </div>
            {tax.serviceChargeEnabled && (
              <div className="mt-3 max-w-xs">
                <FormField
                  label="Service Charge Rate (%)"
                  value={String(tax.serviceChargeRate)}
                  onChange={(v) => setTax({ ...tax, serviceChargeRate: Number(v) })}
                  type="number"
                  step="1"
                />
              </div>
            )}
          </div>

          <div className="p-4 bg-background-100 rounded-lg">
            <p className="text-sm text-foreground-600">
              <strong>Current effective rates:</strong> CGST {tax.cgstRate}% + SGST {tax.sgstRate}% = <span className="font-semibold text-foreground-800">{tax.cgstRate + tax.sgstRate}% GST</span>
              {tax.serviceChargeEnabled && <> · Service Charge: <span className="font-semibold text-foreground-800">{tax.serviceChargeRate}%</span></>}
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-background-200">
            <button
              onClick={saveTax}
              disabled={saving}
              className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Tax Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Printers Section */}
      {activeSection === 'printers' && (
        <div className="space-y-4 max-w-2xl">
          <div className="bg-background-50 border border-background-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground-800 text-base">Printer Devices</h3>
              <button
                onClick={() => { setPrinterForm({ ...emptyPrinter }); setPrinterModalOpen(true); }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-1"></i> Add Printer
              </button>
            </div>
            <div className="space-y-3">
              {printers.map((printer) => (
                <div
                  key={printer.id}
                  className="flex items-start justify-between p-4 rounded-lg border border-background-200 hover:border-primary-200 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-printer-line text-secondary-600 text-base"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground-800 text-sm">{printer.name}</h4>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        {printer.role === 'Billing' ? 'Billing (bills)' : `KOT · ${printer.role}`} · {printer.paper}mm
                      </p>
                      <p className="text-xs text-foreground-400 mt-1">
                        {printer.type === 'network'
                          ? <span className="font-mono">{printer.address}:{printer.port}</span>
                          : <>Windows printer: <span className="font-mono">{printer.address}</span></>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {printer.status && <PrinterStatusBadge status={printer.status} />}
                    <button
                      onClick={() => handleRemovePrinter(printer)}
                      disabled={saving}
                      className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      <i className="ri-delete-bin-line text-red-400 text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {printers.length === 0 && (
              <div className="py-12 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-printer-line text-xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-500">No printers configured</p>
              </div>
            )}
          </div>

          {/* Add Printer Modal */}
          {printerModalOpen && (
            <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]">
              <div className="absolute inset-0 bg-black/30" onClick={() => setPrinterModalOpen(false)}></div>
              <div className="relative w-full max-w-md bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out]">
                <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
                  <h3 className="font-semibold text-foreground-800">Add Printer</h3>
                  <button onClick={() => setPrinterModalOpen(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                    <i className="ri-close-line text-foreground-500"></i>
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Printer Name *</label>
                    <input type="text" value={printerForm.name} onChange={(e) => setPrinterForm({ ...printerForm, name: e.target.value })} placeholder="e.g. Kitchen Main" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Prints</label>
                    <select value={printerForm.role} onChange={(e) => setPrinterForm({ ...printerForm, role: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                      {PRINTER_ROLES.map((r) => <option key={r} value={r}>{r === 'Billing' ? 'Billing (bills)' : `KOT · ${r} station`}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Connection</label>
                    <select value={printerForm.type} onChange={(e) => setPrinterForm({ ...printerForm, type: e.target.value })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                      <option value="network">Network (LAN/WiFi — ESC/POS port 9100)</option>
                      <option value="windows">Windows USB (installed on the POS PC)</option>
                    </select>
                  </div>
                  {printerForm.type === 'network' ? (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-foreground-500 mb-1">Printer IP *</label>
                        <input type="text" value={printerForm.address} onChange={(e) => setPrinterForm({ ...printerForm, address: e.target.value })} placeholder="192.168.1.50" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-500 mb-1">Port</label>
                        <input type="number" value={printerForm.port} onChange={(e) => setPrinterForm({ ...printerForm, port: Number(e.target.value) || 9100 })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-foreground-500 mb-1">Windows printer name *</label>
                      <input type="text" value={printerForm.address} onChange={(e) => setPrinterForm({ ...printerForm, address: e.target.value })} placeholder="exactly as shown in Windows (e.g. TVS RP3160)" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
                      <p className="text-xs text-foreground-400 mt-1">Tip: easier to add USB printers from the POS app — it lists the installed printers for you.</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Paper width</label>
                    <div className="flex gap-2">
                      {[80, 58].map((w) => (
                        <button key={w} onClick={() => setPrinterForm({ ...printerForm, paper: w })} className={`px-4 py-2 text-sm rounded-lg border transition-colors cursor-pointer ${printerForm.paper === w ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium' : 'border-background-200 text-foreground-600 hover:border-background-300'}`}>
                          {w}mm
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200">
                    <button onClick={handleAddPrinter} disabled={saving} className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">Add Printer</button>
                    <button onClick={() => setPrinterModalOpen(false)} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Branding Section */}
      {activeSection === 'branding' && (
        <div className="bg-background-50 border border-background-200 rounded-lg p-6 space-y-5 max-w-2xl">
          <h3 className="font-semibold text-foreground-800 text-base">Branding &amp; Appearance</h3>

          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-background-100">
              <p className="text-sm font-medium text-foreground-700 mb-3">QR Menu Theme</p>
              <div className="grid grid-cols-3 gap-3">
                {themes.map((theme) => {
                  const isSelected = selectedTheme === theme;
                  return (
                    <button
                      key={theme}
                      onClick={() => setSelectedTheme(theme)}
                      className={`p-3 rounded-lg border-2 text-center transition-colors cursor-pointer ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-background-200 hover:border-background-300'
                      }`}
                    >
                      <div className={`w-full h-10 rounded mb-2 ${
                        theme === 'Teal' ? 'bg-primary-500' : theme === 'Warm' ? 'bg-accent-500' : 'bg-foreground-800'
                      }`}></div>
                      <p className="text-xs font-medium text-foreground-700">{theme}</p>
                      {isSelected && (
                        <div className="w-5 h-5 flex items-center justify-center mx-auto mt-1">
                          <i className="ri-check-line text-primary-500 text-xs"></i>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-background-100">
              <p className="text-sm font-medium text-foreground-700 mb-3">Receipt Header Logo</p>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-background-300 flex items-center justify-center bg-background-50 overflow-hidden">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                  ) : (
                    <i className="ri-image-add-line text-xl text-foreground-300"></i>
                  )}
                </div>
                <div>
                  <label className="px-3 py-1.5 text-xs font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap inline-block">
                    <i className="ri-upload-2-line mr-1"></i> Upload Logo
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-foreground-400 mt-1.5">PNG or JPG, max 2MB. Recommended: 200×80px</p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-background-100">
              <p className="text-sm font-medium text-foreground-700 mb-3">Receipt Footer Text</p>
              <textarea
                value={receiptFooter}
                onChange={(e) => setReceiptFooter(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
              ></textarea>
            </div>

            <div className="p-4 rounded-lg bg-background-100">
              <p className="text-sm font-medium text-foreground-700 mb-3">UPI payment QR</p>
              <input
                type="text"
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
                placeholder="yourname@bank"
                className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
              />
              <p className="text-xs text-foreground-400 mt-1.5">
                A scannable UPI QR with the exact bill amount is generated from this ID and shown on the printed bill and the customer display.
              </p>
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showUpiQr}
                  onChange={(e) => setShowUpiQr(e.target.checked)}
                  className="rounded border-background-300"
                />
                <span className="text-sm text-foreground-700">Show UPI QR on the bill &amp; customer display</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-background-200">
            <button
              onClick={saveBranding}
              disabled={saving}
              className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Branding'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({
  label,
  value,
  onChange,
  type = 'text',
  isTextarea = false,
  step,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  isTextarea?: boolean;
  step?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-foreground-500 mb-1">{label}</label>
      {isTextarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 resize-none focus:outline-none focus:border-primary-300"
        ></textarea>
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          step={step}
          className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300"
        />
      )}
    </div>
  );
}

function UpgradeNote({ required, feature, onUpgrade }: { required: Tier; feature: string; onUpgrade: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-8 max-w-2xl text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-50 flex items-center justify-center">
        <i className="ri-lock-2-line text-2xl text-accent-500"></i>
      </div>
      <h3 className="font-semibold text-foreground-800 text-base">Upgrade to {PLAN_LABEL[required]}</h3>
      <p className="text-sm text-foreground-500 mt-1.5">
        Configuring {feature} is part of the {PLAN_LABEL[required]} plan. Upgrade to unlock it.
      </p>
      <button
        onClick={onUpgrade}
        className="mt-4 px-5 py-2 text-sm font-semibold rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
      >
        View plans
      </button>
    </div>
  );
}

function PrinterStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    'Online': 'bg-green-100 text-green-700',
    'Offline': 'bg-red-100 text-red-700',
    'Paper Low': 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium whitespace-nowrap ${styles[status] || 'bg-background-100 text-foreground-600'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'Online' ? 'bg-green-500' : status === 'Offline' ? 'bg-red-500' : 'bg-amber-500'}`}></span>
      {status}
    </span>
  );
}
